import React, {
  useEffect, useCallback, useState,
} from "react";

import { Button } from "antd";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import DragDrop from "../../Env/Analytics/Components/CreateEnvModal/DragDrop";
import ExampleTable from "./ExampleTable";
import FileItem from "./FileItem";

import { getFileNameWithTimestamp, getFilePath, uploadFileToS3WithPresigned } from "../../../utils/helpers";

import { parseExcelFile } from "../../../actions/onboarding";

import "./UploadAnalysisExcel.css";

export default function UploadAnalysisExcel(props) {
  const {
    handleDeleteIconClick,
    currentFile,
    returnFileSize,
    proceedToDataPreview,
    backToUpdateFields,
    selectedType,
    fields,
    updatedLinkReport,
    updatedLinkPatternFields,
    uploadMultipleFiles = false,
  } = props;

  const [selectedFilesMap, setSelectedFilesMap] = useState(new Map()); // all the selected files, including the files uploaded to backend successfully, and unsuccessfully, contains file id and its info
  const [fileNameToFreq, setFileNameToFreq] = useState({});// stores file name and its frequency of selected files, including the files uploaded to backend successfully, and unsuccessfully, used to judge if a file is duplicate
  const [filesData, setFilesData] = useState({}); // stores all the parsed data from uploaded files, if it is a valid excel file. {fileID: {id, name, data}}
  const [loading, setLoading] = useState(false);
  const FILE_SIZE_MAX_LIMIT = 50 * 1048576; // convert 50MB to bytes
  const [nextfileId, setNextFileId] = useState(0); // to give each selected file a unique ID
  const [dragging, setDragging] = useState(false); // Used to change style when dragging files

  /**
   * Return error text if the selected files do not meet requirement. Return null if meet requirements.
   * @param {File} file A selected file
   * @returns {(Object | Null)} Return error text object if there is error, otherwise return null.
   */
  const checkFrontEndFileError = (file, fileNameToFreqObj) => {
    let errorMessages = null;
    if (file.size > FILE_SIZE_MAX_LIMIT) {
      errorMessages = { title: "File size is too big", body: "Max 50 MB", type: "size" };
    } else if (file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" && file.type !== "application/vnd.ms-excel") {
      errorMessages = { title: "File format is not supported", body: "*.xlsx and *.xls only", type: "format" };
    } else if (fileNameToFreqObj[file.path] !== undefined && fileNameToFreqObj[file.path] > 1) {
      errorMessages = { title: "Upload error", body: "This file is duplicate" };
    }

    return errorMessages;
  };

  /**
   * Called when files are selected or dropped. Parse the excel files, store metadata and content data in states.
   */
  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    setDragging(false);

    let selected = []; // store all selected files
    // When uploading multiple files
    if (uploadMultipleFiles) {
      selected.push(...acceptedFiles); // acceptedFiles are the files accepted by the drop-zone library based on parameters we give: accept(format) and maxSize.
      fileRejections.forEach(({ file }) => { // fileRejections are the files rejected. fileRejections: [{errors, file}]
        selected.push(file);
      });
    } else {
      // When uploading single file
      if (acceptedFiles.length === 1) selected = acceptedFiles;
      if (fileRejections.length === 1) selected = [fileRejections[0].file];
    }

    const fileNameToFreqObj = uploadMultipleFiles ? { ...fileNameToFreq } : {};
    selected.forEach((file) => {
      if (fileNameToFreqObj[file.path] !== undefined) {
        fileNameToFreqObj[file.path]++;
      } else {
        fileNameToFreqObj[file.path] = 1;
      }
    });
    setFileNameToFreq(fileNameToFreqObj);

    // When uploading multiple files use old map state, when uploading single file use new map
    const updatedFilesMap = uploadMultipleFiles ? new Map(selectedFilesMap) : new Map();
    // Update error messages of previously added files
    updatedFilesMap.forEach((fileData, fileId) => {
      // Case 1: if a file was uploaded to backend, don't need to check error again.
      // Case 2: if a file has a valid errorMesages, and was failed to upload to backend, i.e. with "backend" type,
      // it already passed size, and format check, we will only show its previous backend error, and don't check front end error again.
      // Otherwise, should check error.
      if ((fileData.errorMessages === null && fileData.apiStatus !== "success")
        || (fileData.errorMessages !== null && fileData.errorMessages.type !== "backend")) {
        const errorMessages = checkFrontEndFileError(fileData, fileNameToFreqObj);
        updatedFilesMap.get(fileId).errorMessages = errorMessages;
      }
    });

    let id = nextfileId;
    const fileIdToData = { ...filesData };
    for (let i = 0; i < selected.length; i++) {
      const file = selected[i];
      const errorMessages = checkFrontEndFileError(file, fileNameToFreqObj);

      const fileMetaData = {
        id, name: file.name, path: file.path, type: file.type, size: file.size, errorMessages, file,
      };

      updatedFilesMap.set(id, fileMetaData);
      id++;
    }
    setNextFileId(id);
    setSelectedFilesMap(updatedFilesMap);
    setFilesData(fileIdToData);
  }, [nextfileId, selectedFilesMap, fileNameToFreq, filesData]);// eslint-disable-line

  /** Called when dragged file enters the DragDrop area, to change UI style of the area */
  const onDragEnter = useCallback(() => {
    setDragging(true);
  }, []);

  /** Called when dragged file leaves the DragDrop area, to change UI style of the area */
  const onDragLeave = useCallback(() => {
    setDragging(false);
  }, []);

  /** Use the drop zone library, passing acceptable file formats, and max size */
  const {
    getRootProps, getInputProps,
  } = useDropzone({
    onDrop,
    onDragEnter,
    onDragLeave,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
    maxSize: FILE_SIZE_MAX_LIMIT,
    maxFiles: 1,
  });

  /**
   * When the delete icon is clicked, toggle the delete confirmation modal
   * @param {*} modalType the tyep of confirmation modal, either "main-modal" or "file-modal"
   * @param {*} showConfirmation boolean value to set the confirmation to be displayed
   * @param {*} file the current selected file
   */
  const toggleDeleteConfirmation = (modalType, showConfirmation, file) => {
    handleDeleteIconClick(modalType, showConfirmation, file);
  };

  /** Called when delete a selected file */
  const handleClickDelete = (fileToDelete) => {
    // Update file name and its frequency
    if (fileNameToFreq[fileToDelete.path] === 1) {
      delete fileNameToFreq[fileToDelete.path];
    } else {
      fileNameToFreq[fileToDelete.path]--;
    }
    setFileNameToFreq({ ...fileNameToFreq });

    // Update shown selected files
    selectedFilesMap.delete(fileToDelete.id);
  };

  /**
   * Keep tracks of the delete file action from the UploadAnalysisExcel component.
   * When users confirmation the delete action from the UploadAnalysisExcel component,
   * the currentFile variable will be changed, then it will call then handleClickDelete() to delete the file
   */
  useEffect(() => {
    if (currentFile) {
      handleClickDelete(currentFile);
    } // eslint-disable-next-line
  }, [currentFile]);

  /**
   * Disable the next button if meet these conditions
   * @returns {boolean}
   */
  const disableNext = () => {
    // if no file is selected yet, disable submit
    if (selectedFilesMap.size === 0) return true;

    let ifDisabledNext = false;
    selectedFilesMap.forEach((fileData) => {
      // if any file has frontend error, disable submit button
      if (fileData.errorMessages !== null && fileData.errorMessages.type !== "backend") {
        ifDisabledNext = true;
      }
      // if any being selected file was fully failed to upload to backend
      if (fileData.apiStatus && fileData.apiStatus === "failed") {
        ifDisabledNext = true;
      }
    });

    return ifDisabledNext;
  };

  /**
   * Handle the Next button onclick.
   * 1. Uploads file to s3 bucket
   * 2. Call api to parse the file
   */
  const handleClickNext = async () => {
    if (selectedFilesMap.size === 0) return;

    setLoading(true);
    const fileData = [...selectedFilesMap.values()][0]; // Take only single file
    if (!fileData || !fileData?.file) return;

    // Upload file to s3 bucket
    const fileName = getFileNameWithTimestamp(fileData.name);

    const fileInfo = {
      file: fileData.file,
      folderPath: "SampleSubmission/ExcelUpload/",
      fileName,
      type: fileData.type,
    };
    const filePath = getFilePath(fileInfo);
    const uploadObj = await uploadFileToS3WithPresigned([fileInfo], "private");

    if (!uploadObj || !uploadObj[filePath]) {
      fileData.errorMessages = {
        title: "Upload failed",
        body: "Unable to upload file",
      };
      setSelectedFilesMap(new Map([
        [fileData.id, fileData],
      ]));
      setLoading(false);
      return;
    }
    const baseUrl = uploadObj[filePath].url;

    // Call excelparser/
    const response = await parseExcelFile(selectedType, baseUrl, filePath);
    if (!response.success) {
      fileData.errorMessages = {
        title: "Failed to parse",
        body: response.message || "Unable to parse file data",
      };
      setSelectedFilesMap(new Map([
        [fileData.id, fileData],
      ]));
      setLoading(false);
      return;
    }

    toast.success("Uploaded file successfully. Please wait while the file is getting processed.");
    proceedToDataPreview();
    setLoading(false);
  };

  const downloadTemplate = () => {
    try {
      const allFields = fields[selectedType] || [];
      const linkNames = selectedType === "product" ? (updatedLinkReport || []) : (updatedLinkPatternFields || []);
      const TOTAL_ANALYSIS_COLS = 3;
      const linkGroupLabel = selectedType === "product" ? "Link Report Fields" : "Link Pattern Fields";

      const link = allFields.filter((f) => linkNames.includes(f.json_field));
      const autofill = allFields.filter((f) => (
        !linkNames.includes(f.json_field)
          && (f.field_autofill === "1" || f.test_autofill === "1")
      ));

      const groupRow = [
        ...link.map(() => linkGroupLabel),
        ...autofill.map(() => "Autofill Fields"),
        ...Array(TOTAL_ANALYSIS_COLS).fill("Analysis"),
      ];
      const headerRow = [
        ...link.map((f) => f.title_field),
        ...autofill.map((f) => f.title_field),
        ...Array(TOTAL_ANALYSIS_COLS).fill(""),
      ];

      const ws = XLSX.utils.aoa_to_sheet([groupRow, headerRow]);

      // Merge each section's group-row cells so it reads as a single grouped
      // header (matching the "Link Report"/"Autofill"/"Analysis" section
      // grouping shown in the ExampleTable preview), instead of repeating
      // the section label in every column.
      const merges = [];
      let colIndex = 0;
      [link.length, autofill.length, TOTAL_ANALYSIS_COLS].forEach((sectionLength) => {
        if (sectionLength > 1) {
          merges.push({
            s: { r: 0, c: colIndex },
            e: { r: 0, c: colIndex + sectionLength - 1 },
          });
        }
        colIndex += sectionLength;
      });
      ws["!merges"] = merges;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      XLSX.writeFile(wb, `upload_template_${selectedType}.xlsx`);
    } catch (err) {
      toast.error("Failed to download template. Please try again.");
    }
  };

  /** To get a list of selected files to display */
  const getFileItems = () => [...selectedFilesMap.values()].map((fileData) => (
    <FileItem
      key={fileData.id}
      fileData={fileData}
      toggleDeleteConfirmation={toggleDeleteConfirmation}
      returnFileSize={returnFileSize}
    />
  ));

  return (
    <>
      <div className="create-env-modal-body">
        <section className="env-upload-files">
          <div className={`env-drag-drop-files ${dragging ? "env-upload-files-dragging" : ""}`}>
            <DragDrop
              uploadTarget="your files"
              getInputProps={getInputProps}
              getRootProps={getRootProps}
            />
          </div>
          <aside className="env-uploaded-files">
            {selectedFilesMap.size === 0
              ? (
                <div className="env-uploaded-files-no-file">
                  <span>No Files Uploaded</span>
                  <div className="UploadAnalysisExcel__ExampleTableWrapper">
                    <ExampleTable
                      selectedType={selectedType}
                      fields={fields}
                      updatedLinkReport={updatedLinkReport}
                      updatedLinkPatternFields={updatedLinkPatternFields}
                      onDownload={downloadTemplate}
                    />
                  </div>
                </div>
              )
              : (
                <>
                  <h3>Uploaded Files</h3>
                  <StyledScrollbar className="env-uploaded-files-scroll-bar">
                    <aside className="env-uploaded-files-innter">
                      <ul>{getFileItems()}</ul>
                    </aside>
                  </StyledScrollbar>
                </>
              )}
          </aside>
        </section>
      </div>

      <div className="UploadAnalysisExcel__ButtonContainer">
        <Button
          type="default"
          onClick={backToUpdateFields}
          disabled={loading}
        >
          Back
        </Button>
        <Button
          type="primary"
          onClick={handleClickNext}
          disabled={disableNext()}
          loading={loading}
        >
          Submit
        </Button>
      </div>
    </>
  );
}
